import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../api/client";
import { IconLoader, IconAlert } from "../components/Icons";

interface Item {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_cost: number | null;
}

interface ReceiptDetail {
  id: string;
  receipt_number: string | null;
  status: string;
  movement_type: string;
  warehouse_name: string;
  source_warehouse_name: string | null;
  supplier_name: string | null;
  received_at: string;
  total_cost: number;
  note: string | null;
  update_product_cost: boolean;
  items: Item[];
}

const fmtMoney = (v: number | null) => (v === null ? "—" : `${(v / 100).toFixed(2)} Kč`);
const fmtQty = (v: number) => parseFloat(v.toFixed(3)).toLocaleString("cs-CZ");

export default function StockReceiptDetail() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [receipt, setReceipt] = useState<ReceiptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<ReceiptDetail>(`/stock-receipts/${id}`)
      .then(({ data }) => setReceipt(data))
      .catch((e) => setErr(apiError(e, t("stockReceipt.loadError"))))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;
  if (err || !receipt) {
    return (
      <div className="card" style={{ padding: 16, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}>
        <IconAlert size={16} /> {err || t("stockReceipt.loadError")}
      </div>
    );
  }

  const isReceipt = receipt.movement_type === "receipt";

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{t("stockReceipt.detailTitle")}</h1>
          <p>{t(`stockReceipt.${receipt.movement_type}` as never)} · {new Date(receipt.received_at).toLocaleString("cs-CZ")}</p>
        </div>
        <button className="btn-ghost" onClick={() => nav(-1)}>{t("common.back")}</button>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, fontSize: 14 }}>
          <div>
            <div style={{ color: "var(--muted)", fontSize: 12 }}>{t("stockReceipt.document")}</div>
            <div>{receipt.receipt_number || "—"}</div>
          </div>
          <div>
            <div style={{ color: "var(--muted)", fontSize: 12 }}>{t("stockReceipt.type")}</div>
            <div>{t(`stockReceipt.${receipt.movement_type}` as never)}</div>
          </div>
          <div>
            <div style={{ color: "var(--muted)", fontSize: 12 }}>{t("common.status")}</div>
            <div>
              <span style={{
                padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                background: receipt.status === "draft" ? "var(--accent-dim)" : "var(--green-dim)",
                color: receipt.status === "draft" ? "var(--accent)" : "var(--green)",
              }}>{t(`stockReceipt.status.${receipt.status}` as never)}</span>
            </div>
          </div>
          <div>
            <div style={{ color: "var(--muted)", fontSize: 12 }}>{t("stockReceipt.warehouse")}</div>
            <div>{receipt.warehouse_name}</div>
          </div>
          {receipt.source_warehouse_name && (
            <div>
              <div style={{ color: "var(--muted)", fontSize: 12 }}>{t("stockReceipt.source")}</div>
              <div>{receipt.source_warehouse_name}</div>
            </div>
          )}
          {receipt.supplier_name && (
            <div>
              <div style={{ color: "var(--muted)", fontSize: 12 }}>{t("stockReceipt.supplierName")}</div>
              <div>{receipt.supplier_name}</div>
            </div>
          )}
          {isReceipt && (
            <div>
              <div style={{ color: "var(--muted)", fontSize: 12 }}>{t("stockReceipt.updateCost")}</div>
              <div>{receipt.update_product_cost ? t("common.yes") : t("common.no")}</div>
            </div>
          )}
          {receipt.note && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ color: "var(--muted)", fontSize: 12 }}>{t("stockReceipt.note")}</div>
              <div>{receipt.note}</div>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ overflow: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>{t("stockReceipt.product")}</th>
              <th style={{ textAlign: "right" }}>{t("stockReceipt.quantity")}</th>
              {isReceipt && <th style={{ textAlign: "right" }}>{t("stockReceipt.unitCost")}</th>}
              {isReceipt && <th style={{ textAlign: "right" }}>{t("stockReceipt.totalCost")}</th>}
            </tr>
          </thead>
          <tbody>
            {receipt.items.map((it) => (
              <tr key={it.id}>
                <td>{it.product_name}</td>
                <td style={{ textAlign: "right" }}>{fmtQty(it.quantity)}</td>
                {isReceipt && <td style={{ textAlign: "right" }}>{fmtMoney(it.unit_cost)}</td>}
                {isReceipt && <td style={{ textAlign: "right" }}>{it.unit_cost === null ? "—" : fmtMoney(Math.round(it.quantity * it.unit_cost))}</td>}
              </tr>
            ))}
          </tbody>
          {isReceipt && (
            <tfoot>
              <tr>
                <td colSpan={3} style={{ textAlign: "right", fontWeight: 600 }}>{t("stockReceipt.grandTotal")}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(receipt.total_cost)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
